import classNames from 'classnames';
import { useRouter } from 'next/router';
import ArrowSVG from 'public/icons/arr.svg';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Format } from '@/entities/format';
import { Place } from '@/entities/place';
import { AppProgramEvent } from '@/entities/program';
import { transformProgramFromApi } from '@/shared/api/lib/utils/transform-program';
import { createPdf } from '@/shared/api/postFavouritesPrograms';
import { ApiProgramRequest, AppProgramResponse, useProgramQuery } from '@/shared/api/program';
import Dropdown from '@/shared/components/shared/Dropdown';
import { DICT } from '@/shared/config/dict';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useLocale } from '@/shared/hooks/use-locale';
import { tp } from '@/shared/lib/typograf';
import { encodeUrl } from '@/shared/lib/utils/filter/chpu/encode-url';
import getNoun from '@/shared/lib/utils/get-noun';
import { deleteEmptyFieldsPdf } from '@/shared/lib/utils/pdf/filterPrograms';
import Button from '../../shared/Button';
import Loader from '../../shared/Loader';
import Reveal from '../../shared/Reveal';
import SearchInput from '../../shared/SearchInput';
import Spinner from '../../shared/Spinner';
import Typografed from '../../shared/Typografed';
import ProgramDetailList from '../ProgramDetailList';
import { getDayTitle } from './ProgramDetailSection.helpers';

export type ProgramDetailCard = AppProgramEvent;

export type ProgramDetailSectionProps = {
    cards: ProgramDetailCard[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    reflectFilterStateInUrl?: boolean;
    programDetailSectionProps: ProgramDetailSectionProps;
    isButtonVisible?: boolean;
    initialParams: ApiProgramRequest;
    initialResponse: AppProgramResponse;
    days: Day[];
    formats: Format[];
    places: Place[];
    directions: Direction[];
    disableDirections?: boolean;
    isHrSummit?: boolean;
    visibleCardsCount?: number;
    mainTheme?: string;
    pdfTitle?: string;
};

const ProgramDetailSection = forwardRef<HTMLDivElement, Props>(
    (
        {
            reflectFilterStateInUrl = false,
            days,
            formats,
            directions,
            programDetailSectionProps,
            isButtonVisible,
            initialResponse,
            places,
            initialParams = {},
            disableDirections = false,
            isHrSummit = false,
            visibleCardsCount = 10,
            mainTheme,
            pdfTitle,
        },
        ref,
    ) => {
        const router = useRouter();
        const locale = useLocale();

        const [params, setParams] = useState<ApiProgramRequest>(() => {
            if (typeof window !== 'undefined' && reflectFilterStateInUrl) {
                router.replace(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...initialParams },
                    },
                    encodeUrl(router.asPath, initialParams),
                    { shallow: true, scroll: false },
                );
            }

            return initialParams;
        });

        const [searchTerm, setSearchTerm] = useState(params.q || '');
        const debouncedSearchTerm = useDebounce(searchTerm, 500);

        const { data, isFetching, error } = useProgramQuery(
            { params: { ...params, days: typeof params.day === 'number' ? [params.day] : days.map((day) => day.id) } },
            initialResponse,
        );

        const hasInitialFilters = Boolean(initialParams.day || initialParams.directions?.length || initialParams.q);
        const { data: allProgramsData } = useProgramQuery(
            { params: { days: days.map((day) => day.id) } },
            hasInitialFilters ? undefined : initialResponse,
        );

        const cards = data?.data?.items || programDetailSectionProps.cards;
        const transformedCards = transformProgramFromApi(cards, directions, places, formats, days);
        const selectedDay = days.find((item) => item.id === params.day);
        const allCards = allProgramsData?.data?.items || programDetailSectionProps.cards;
        const availableDirections = directions.filter((dir) =>
            allCards.some((card) => card.directions.includes(dir.id)),
        );
        const totalCount = data?.data?.totalCount || cards.length;

        const isFullProgram = !params.day && !params.directions?.length && !params.q;

        useEffect(() => {
            const handler = () => {
                const dayIds = new Set(days.map((day) => day.id));
                const cardsWithKnownDay = cards.filter((card) => dayIds.has(card.day));
                const transformed = transformProgramFromApi(cardsWithKnownDay, directions, places, formats, days);
                const pdfCards = deleteEmptyFieldsPdf(transformed);
                createPdf(pdfCards, { title: pdfTitle || '', theme: mainTheme || '' }, isFullProgram);
            };

            document.addEventListener('startDownloadPdf', handler);

            return () => {
                document.removeEventListener('startDownloadPdf', handler);
            };
        }, [cards, days, directions, places, formats, pdfTitle, mainTheme, isFullProgram]);

        const handleDayChange = useCallback(
            (dayId?: number) => {
                setParams((prevParams) => {
                    const newParams = { ...prevParams };

                    if (dayId === prevParams.day) {
                        delete newParams.day;
                    } else if (dayId !== undefined) {
                        newParams.day = dayId;
                    } else {
                        delete newParams.day;
                    }

                    if (reflectFilterStateInUrl) {
                        router.push(
                            {
                                pathname: router.pathname,
                                query: { ...router.query, ...newParams },
                            },
                            encodeUrl(router.asPath, newParams),
                            { shallow: true, scroll: false },
                        );
                    }

                    return newParams;
                });
            },
            [reflectFilterStateInUrl, router],
        );

        const handleDirectionChange = useCallback(
            (directionId: number) => {
                setParams((prevParams) => {
                    const newParams = { ...prevParams };

                    newParams.directions = Array.isArray(newParams.directions) ? [...newParams.directions] : [];

                    if (newParams.directions.includes(directionId)) {
                        newParams.directions = newParams.directions.filter((id) => id !== directionId);
                    } else {
                        newParams.directions.push(directionId);
                    }

                    if (reflectFilterStateInUrl) {
                        router.push(
                            {
                                pathname: router.pathname,
                                query: { ...router.query, ...newParams },
                            },
                            encodeUrl(router.asPath, newParams),
                            { shallow: true, scroll: false },
                        );
                    }

                    return newParams;
                });
            },
            [reflectFilterStateInUrl, router],
        );

        const handleSearchInput = useCallback((e: any) => {
            setSearchTerm(e.target.value);
        }, []);

        useEffect(() => {
            setParams((prevParams) => {
                const nextQ = debouncedSearchTerm || undefined;

                if ((prevParams.q || undefined) === nextQ) {
                    return prevParams;
                }

                const newParams = { ...prevParams };

                if (nextQ) {
                    newParams.q = nextQ;
                } else {
                    delete newParams.q;
                }

                if (reflectFilterStateInUrl) {
                    router.push(
                        {
                            pathname: router.pathname,
                            query: { ...router.query, ...newParams },
                        },
                        encodeUrl(router.asPath, newParams),
                        { shallow: true, scroll: false },
                    );
                }

                return newParams;
            });
        }, [debouncedSearchTerm, reflectFilterStateInUrl, router]);

        return (
            <>
                <section className="program-detail-section-wrapper wrapper">
                    <div className="news-top-block">
                        <div className="program-filters">
                            <FiltersDefault
                                days={days}
                                directions={!disableDirections && !isHrSummit ? availableDirections : []}
                                selectedDay={selectedDay}
                                selectedDirIds={params.directions}
                                handleDayChange={handleDayChange}
                                handleDirectionChange={handleDirectionChange}
                                totalCount={totalCount}
                            />
                            <FiltersMobile
                                days={days}
                                directions={!disableDirections && !isHrSummit ? availableDirections : []}
                                selectedDay={selectedDay}
                                selectedDirIds={params.directions}
                                handleDayChange={handleDayChange}
                                handleDirectionChange={handleDirectionChange}
                                totalCount={totalCount}
                            />

                            <div className="program-filters__search program-filters__group">
                                <p className="program-filters__group-title program-filters__search-title text-xs">
                                    {DICT.SEARCH[locale]}
                                </p>
                                <div className="program-filters__group-inner">
                                    <SearchInput
                                        value={searchTerm}
                                        onInput={handleSearchInput}
                                        placeholder="Поиск по программе"
                                    />
                                </div>
                                <div className="program-filters__search-count">
                                    <p className="program-filters__group-title text-opacity text-xs">Найдено:</p>
                                    <ProgramsCount totalCount={totalCount} isFetching={isFetching} />
                                </div>
                            </div>
                        </div>
                    </div>
                    {transformedCards.length > 0 ? (
                        <ProgramDetailList
                            isFetching={isFetching}
                            cards={transformedCards}
                            isDayVisible={!selectedDay}
                            isButtonVisible={isButtonVisible}
                            visibleCardsCount={visibleCardsCount}
                            disableDirections={disableDirections}
                            mainTheme={mainTheme}
                            pdfTitle={pdfTitle}
                        />
                    ) : (
                        !isFetching && (
                            <Reveal>
                                <div className="text-l news-list-not-found">
                                    <Typografed>
                                        {Object.values({ ...params, limit: null }).filter(
                                            (value) =>
                                                Boolean(value) && (Array.isArray(value) ? value.length > 0 : true),
                                        ).length > 0
                                            ? 'Ничего не найдено. Попробуйте изменить запрос и мы поищем еще раз. '
                                            : 'Программы не найдены'}
                                    </Typografed>
                                </div>
                            </Reveal>
                        )
                    )}
                </section>
                <Loader isFetching={isFetching} />
            </>
        );
    },
);

type FiltersProps = {
    selectedDay?: Day;
    selectedDirIds?: Direction['id'][];
    days: Day[];
    directions: Direction[];
    totalCount: number;

    handleDayChange: (day: number | undefined) => void;
    handleDirectionChange: (dir: number) => void;
};

const ProgramsCount = ({ totalCount, isFetching }: { totalCount: number; isFetching: boolean }) => (
    <div className="program-filters__count text-xs">
        {isFetching ? (
            <Spinner className="program-filters__count-spinner" />
        ) : (
            <>
                {totalCount} {getNoun(totalCount, 'событие', 'события', 'событий')}
            </>
        )}
    </div>
);

const FiltersDefault = ({
    days,
    directions,
    selectedDay,
    selectedDirIds,
    handleDayChange,
    handleDirectionChange,
}: FiltersProps) => {
    const locale = useLocale();

    return (
        <div className="program-filters__default">
            {days.length > 0 && (
                <div className="program-filters__group program-filters__group--noshrink">
                    <p className="program-filters__group-title text-xs text-opacity">{tp(DICT.FORUM_DAYS[locale])}</p>

                    <div className="program-filters__group-inner">
                        {days.map((day, index) => {
                            return (
                                <Button
                                    key={day.id}
                                    type="button"
                                    className={classNames({ active: day.id === selectedDay?.id })}
                                    variant="grey-rounded-checkable"
                                    onClick={() => handleDayChange(day.id)}
                                >
                                    {getDayTitle(day)}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            )}

            {directions.length > 0 && (
                <div className="program-filters__group">
                    <p className="program-filters__group-title text-xs text-opacity">{tp('Направление участия')}:</p>

                    <div className="program-filters__group-inner program-filters__group-inner--wrap">
                        {directions.map((dir, index) => {
                            return (
                                <Button
                                    key={dir.id}
                                    type="button"
                                    className={classNames({ active: selectedDirIds?.includes(dir.id) })}
                                    variant="grey-rounded-checkable"
                                    onClick={() => handleDirectionChange(dir.id)}
                                >
                                    {dir.name}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const FiltersMobile = ({
    days,
    directions,
    selectedDay,
    selectedDirIds,
    handleDayChange,
    handleDirectionChange,
    totalCount,
}: FiltersProps) => {
    const locale = useLocale();
    return (
        <div className="program-filters__mobile">
            {days.length > 0 && (
                <Dropdown>
                    <Dropdown.Toggler className="news-select-btn">
                        <div className="text-xs">
                            {selectedDay ? getDayTitle(selectedDay) : tp(DICT.ALL_DAYS[locale])}
                        </div>
                        <ArrowSVG className="news-arrow-svg" />
                    </Dropdown.Toggler>

                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                        <ul className="list-unstyled menu-dropdown-list">
                            <li>
                                <Dropdown.Picker
                                    onClick={() => handleDayChange(undefined)}
                                    className={classNames('news-select-item text-xs', {
                                        'is-active': !selectedDay,
                                    })}
                                >
                                    {tp(DICT.ALL_DAYS[locale])}
                                </Dropdown.Picker>
                            </li>
                            {days.map((item, i) => {
                                return (
                                    <li key={i}>
                                        <Dropdown.Picker
                                            onClick={() => handleDayChange(item.id)}
                                            className={classNames('news-select-item text-xs', {
                                                'is-active': selectedDay?.id === item.id,
                                            })}
                                        >
                                            {getDayTitle(item)}
                                        </Dropdown.Picker>
                                    </li>
                                );
                            })}
                        </ul>
                    </Dropdown.Content>
                </Dropdown>
            )}

            {directions.length > 0 && (
                <Dropdown>
                    <Dropdown.Toggler className="news-select-btn">
                        <div className="text-xs">{tp(DICT.PARTICIPATION_FORMAT[locale])}</div>
                        <ArrowSVG className="news-arrow-svg" />
                    </Dropdown.Toggler>
                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                        <ul className="list-unstyled menu-dropdown-list">
                            {directions?.map((direction) => (
                                <li key={direction.id}>
                                    <Dropdown.Picker
                                        className={classNames('news-select-item text-xs', {
                                            'is-active': selectedDirIds?.includes(direction.id),
                                        })}
                                        onClick={() => handleDirectionChange(direction.id)}
                                        keepOpen
                                    >
                                        {direction.name}
                                    </Dropdown.Picker>
                                </li>
                            ))}
                        </ul>
                    </Dropdown.Content>
                </Dropdown>
            )}
        </div>
    );
};
ProgramDetailSection.displayName = 'ProgramDetailSection';

export default ProgramDetailSection;
